import type { ReactNode } from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * Route-level authorization guard. Renders `children` only when `allow(role)`
 * is true; otherwise shows a clear "restricted" notice.
 *
 * This is UI gating only — the authoritative enforcement lives in the backend
 * (RLS policies and the `user_has_role` checks inside the trusted RPCs). The
 * guard exists so a non-permitted user can never reach a sensitive screen or
 * accidentally attempt an action that the backend would reject anyway.
 */
export function RouteGuard({
  allow,
  title,
  children,
}: {
  allow: (role: string | null) => boolean;
  title: string;
  children: ReactNode;
}) {
  const { role } = useAuth();

  if (allow(role)) return <>{children}</>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">{title}</h1>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
          <i className="ri-lock-line text-slate-400 text-xl" aria-hidden="true" />
        </div>
        <p className="text-sm text-slate-600">You don't have permission to view this area.</p>
        <p className="text-xs text-slate-400 mt-1">Ask an administrator if you need access.</p>
      </div>
    </div>
  );
}